import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  CircuitBoard,
  Inbox,
  Linkedin,
  Mail,
  MessageCircle,
  Radar,
  Send,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { ProductCard } from "@/components/ProductCard";



const fallbackPortfolio = {
  profile: {
    name: "Dani Rosady",
    role: "Software Developer & IoT Engineer",
    tagline:
      "Building dependable software products and connected systems for enterprises, public institutions, and ambitious startups.",
    intro:
      "Dani helps teams translate complex operational challenges into modern web platforms, field-ready IoT solutions, and executive-friendly dashboards that move projects from concept to deployment.",
    capabilities: [
      "Full-stack product delivery",
      "IoT telemetry and edge integration",
      "Operations dashboards and analytics",
      "Workflow automation",
      "Technical discovery for cross-functional teams",
    ],
    industries: ["Enterprises", "Government", "Startups"],
  },
  metrics: [
    { value: "Web Apps", label: "Product systems designed for clarity and speed" },
    { value: "IoT Ops", label: "Connected infrastructure with decision-ready telemetry" },
    { value: "Dashboards", label: "Executive reporting for daily operations" },
  ],
  highlights: [
    {
      title: "Software and hardware thinking in one delivery loop",
      description:
        "From interfaces and APIs to sensors and device data, the build process stays cohesive end to end.",
    },
    {
      title: "Designed for procurement, operators, and decision makers",
      description:
        "Clear information architecture keeps enterprise and public-sector stakeholders aligned.",
    },
    {
      title: "Modern UX without losing operational rigor",
      description:
        "Clean interfaces, dependable flows, and measurable outcomes are prioritized together.",
    },
  ],
  hero_image: "/img/5b4d1f6e7aaabceafee045697dd31deb1881a2d56424e64750fb07e5df56095a.png",
  booking_url: "https://cal.com/danirosady/30min",
  contact_methods: [
    { kind: "email", label: "Email", value: "hello@danirosady.com", href: "mailto:hello@danirosady.com" },
    {
      kind: "linkedin",
      label: "LinkedIn",
      value: "linkedin.com/in/danirosady",
      href: "https://www.linkedin.com/in/danirosady",
    },
    {
      kind: "whatsapp",
      label: "WhatsApp",
      value: "+62 821-5647-1103",
      href: "https://wa.me/6282156471103?text=Hi%20Dani%2C%20Saya%27d%20ingin%20to%20diskusi%20tentang%20project.",
    },
  ],
  products: [
    {
      id: "wiring-studio-ai",
      title: "Wiring Studio (AI)",
      audience: "Featured IoT",
      summary: "AI-assisted workspace for simpler IoT device management, provisioning, and deployment.",
      capabilities: ["Device registry", "Deployment flows", "AI-assisted operations"],
      cover_image: "/img/photo-1585387047604-b4bab9250385.jpeg",
      screenshots: [
        {
          title: "Fleet Overview",
          caption: "Operational visibility for connected devices, status, and rollout progress.",
          metric: "Device orchestration",
          accent: "AI workflow",
        },
        {
          title: "Deployment Board",
          caption: "Release-ready workflows for provisioning, updates, and environment sync.",
          metric: "Rollout control",
          accent: "Edge deployment",
        },
        {
          title: "Signal Insights",
          caption: "Actionable status summaries for teams handling large IoT estates.",
          metric: "Operational clarity",
          accent: "Monitoring view",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-5s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["IoT Platform", "AI Ops", "Deployment"],
    },
    {
      id: "agri-command-center",
      title: "Agri Command Center",
      audience: "Government",
      summary: "Edge-to-cloud agricultural intelligence hub connecting IoT acquisition for government oversight and investment visibility.",
      capabilities: ["Edge acquisition", "Agri dashboards", "Stakeholder reporting"],
      cover_image: "/img/pexels-photo-3308359.jpeg",
      screenshots: [
        {
          title: "Field Monitoring",
          caption: "A single view for agricultural assets, spatial operations, and acquisition status.",
          metric: "Regional oversight",
          accent: "Agri intelligence",
        },
        {
          title: "Command Reporting",
          caption: "Decision-ready dashboards for public-sector teams and investment stakeholders.",
          metric: "Executive preview",
          accent: "Data command",
        },
        {
          title: "Edge Stream",
          caption: "Reliable data flow from edge devices into a unified command environment.",
          metric: "Realtime acquisition",
          accent: "IoT pipeline",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-10s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["AgriTech", "Government", "Command Center"],
    },
    {
      id: "simpeg-plus",
      title: "SIMPEG+",
      audience: "Government",
      summary: "Integrated civil-service workforce management platform for records, workflows, and public-sector decision support.",
      capabilities: ["HR workflows", "Employee records", "Public-sector reporting"],
      cover_image: "/img/0dec509a8b49692fd0ddea3956098fca27e0d48b91de14ad53ff3cead84a1c15.png",
      screenshots: [
        {
          title: "Employee Overview",
          caption: "Integrated profile, status, and administrative information for ASN operations.",
          metric: "Unified records",
          accent: "Workforce view",
        },
        {
          title: "Workflow Approval",
          caption: "Structured process layers for staffing actions, updates, and validations.",
          metric: "Operational governance",
          accent: "Approval flow",
        },
        {
          title: "Decision Dashboard",
          caption: "Fast access to personnel trends and actionable management summaries.",
          metric: "Management support",
          accent: "Executive panel",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-15s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["ASN", "HRIS", "Integrated System"],
    },
    {
      id: "smart-takmir-masjid",
      title: "Smart Takmir Masjid",
      audience: "Community",
      summary: "Digital mosque operations with smart TV scheduling for adzan, khutbah, and recurring announcements.",
      capabilities: ["Prayer content", "Display scheduling", "Masjid operations"],
      cover_image: "/img/more_products/pexels-photo-36195156.webp",
      screenshots: [
        {
          title: "Schedule Board",
          caption: "Structured display planning for adzan, khutbah, and prayer-related content.",
          metric: "Live schedule",
          accent: "Masjid display",
        },
        {
          title: "TV Experience",
          caption: "Clean visual delivery for worship spaces using smart-screen placement.",
          metric: "Audience-ready",
          accent: "Smart signage",
        },
        {
          title: "Takmir Control",
          caption: "Simple content and timing control for daily and weekly operations.",
          metric: "Easy management",
          accent: "Community tech",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-5s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["Smart TV", "Mosque Tech", "Scheduling"],
    },
    {
      id: "smart-managed-closed-house-poultry",
      title: "Smart Managed Closed House Poultry",
      audience: "AgriTech",
      summary: "Sensor-led poultry house monitoring and environmental control for more stable farm operations.",
      capabilities: ["Farm monitoring", "Environmental control", "Production alerts"],
      cover_image: "/img/more_products/pexels-photo-2255825.webp",
      screenshots: [
        {
          title: "House Conditions",
          caption: "Temperature, humidity, and operational status surfaced in one monitoring layer.",
          metric: "Condition tracking",
          accent: "Closed house",
        },
        {
          title: "Production Control",
          caption: "A structured view for healthier and more efficient poultry operations.",
          metric: "Farm response",
          accent: "Automation",
        },
        {
          title: "Issue Alerts",
          caption: "Quick notification flow for operational anomalies that need attention.",
          metric: "Action ready",
          accent: "Ops alert",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-10s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["Poultry", "Automation", "Farm Ops"],
    },
    {
      id: "smart-green-house",
      title: "Smart Green House",
      audience: "AgriTech",
      summary: "Self-managed greenhouse platform for smarter agriculture, climate control, and crop operations.",
      capabilities: ["Climate control", "Crop monitoring", "Self-managed operations"],
      cover_image: "/img/more_products/pexels-photo-2886932.jpeg",
      screenshots: [
        {
          title: "Greenhouse Overview",
          caption: "A calm control surface for environmental conditions and crop health context.",
          metric: "Climate visibility",
          accent: "Smart growing",
        },
        {
          title: "Automation Rules",
          caption: "Set thresholds and automated responses for more consistent greenhouse performance.",
          metric: "Self-managed",
          accent: "Control logic",
        },
        {
          title: "Crop Tracking",
          caption: "Simple monitoring for daily growth operations and agricultural planning.",
          metric: "Plant operations",
          accent: "Agri dashboard",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-15s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["Greenhouse", "Agriculture", "Automation"],
    },
    {
      id: "pengembangan-kompetensi-asn",
      title: "Pengembangan Kompetensi ASN",
      audience: "Government",
      summary: "MOOC-based learning platform for structured civil-service competency development and upskilling programs.",
      capabilities: ["MOOC delivery", "Learning paths", "Competency tracking"],
      cover_image: "/img/more_products/5b4d1f6e7aaabceafee045697dd31deb1881a2d56424e64750fb07e5df56095a.png",
      screenshots: [
        {
          title: "Learning Dashboard",
          caption: "A modern course overview for ongoing programs, enrolments, and progress.",
          metric: "Program visibility",
          accent: "MOOC app",
        },
        {
          title: "Course Pathways",
          caption: "Competency-driven learning journeys designed for public-sector development goals.",
          metric: "Structured upskilling",
          accent: "Learning flow",
        },
        {
          title: "Admin Reporting",
          caption: "Easy review of participation, progress, and institutional learning outcomes.",
          metric: "Training insights",
          accent: "Capability growth",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-5s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["MOOC", "ASN", "Learning Platform"],
    },
    {
      id: "absensi-face-recognition",
      title: "Absensi Face Recognition",
      audience: "Mobile",
      summary: "Mobile and mass attendance verification experience powered by face recognition and rapid presence capture.",
      capabilities: ["Mobile presence", "Face verification", "Mass attendance"],
      cover_image: "/img/more_products/photo-1703080666768-3ea98de3a114.jpeg",
      screenshots: [
        {
          title: "Recognition Flow",
          caption: "Fast identity capture for attendance moments that need speed and accuracy.",
          metric: "Quick check-in",
          accent: "Face scan",
        },
        {
          title: "Mobile Experience",
          caption: "Built for handheld verification in field, office, and event-style environments.",
          metric: "On-device access",
          accent: "Mobile first",
        },
        {
          title: "Presence Overview",
          caption: "Attendance visibility for teams managing large-scale or distributed check-ins.",
          metric: "Mass presence",
          accent: "Realtime record",
        },
      ],
      video_url: "https://samplelib.com/lib/preview/mp4/sample-10s.mp4",
      demo_url: "https://cal.com/danirosady/30min",
      tags: ["Attendance", "Face ID", "Mobile App"],
    },
  ],
};

const initialForm = {
  name: "",
  email: "",
  company: "",
  project_type: "",
  message: "",
};

const iconMap = {
  email: Mail,
  linkedin: Linkedin,
  whatsapp: MessageCircle,
};

export const PortfolioLanding = () => {
  const [portfolio] = useState(fallbackPortfolio);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [showMoreProducts, setShowMoreProducts] = useState(false);
  const [carouselApi, setCarouselApi] = useState();
  const [activeSlide, setActiveSlide] = useState(0);

  const heroBadges = useMemo(
    () => [
      { icon: CircuitBoard, text: "Full-stack product systems" },
      { icon: Radar, text: "IoT dashboards and telemetry" },
      { icon: ShieldCheck, text: "Enterprise-ready delivery" },
    ],
    [],
  );

  const featuredProducts = useMemo(() => portfolio.products.slice(0, 3), [portfolio.products]);
  const moreProducts = useMemo(() => portfolio.products.slice(3), [portfolio.products]);

  useEffect(() => {
    if (!carouselApi) {
      return undefined;
    }

    const updateActiveSlide = () => {
      setActiveSlide(carouselApi.selectedScrollSnap());
    };

    updateActiveSlide();
    carouselApi.on("select", updateActiveSlide);
    carouselApi.on("reInit", updateActiveSlide);

    return () => {
      carouselApi.off("select", updateActiveSlide);
      carouselApi.off("reInit", updateActiveSlide);
    };
  }, [carouselApi]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const encoded = new URLSearchParams({
        "form-name": "contact",
        ...formData,
      }).toString();

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded,
      });

      if (!response.ok) {
        throw new Error("Something went wrong.");
      }

      toast.success("Thanks — your message has been sent.");
      setFormData(initialForm);
    } catch (error) {
      toast.error("The message could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-shell text-slate-50">
      <div className="ambient-orb -left-20 top-10 h-72 w-72 bg-cyan-400/30" />
      <div className="ambient-orb right-0 top-96 h-64 w-64 bg-amber-300/20" style={{ animationDelay: "3s" }} />

      <header className="section-shell sticky top-0 z-50 px-4 pt-4 sm:px-6">
        <div className="glass-panel mx-auto flex max-w-7xl items-center justify-between rounded-full px-4 py-3 sm:px-6">
          <a href="#top" className="flex items-center gap-3" data-testid="navbar-brand-link">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-300 text-sm font-bold text-slate-950">
              DR
            </span>
            <div>
              <p className="text-sm font-semibold text-white" data-testid="navbar-brand-name">Dani Rosady</p>
              <p className="text-xs text-slate-400">Software Developer & IoT Engineer</p>
            </div>
          </a>

          <nav className="hidden items-center gap-6 md:flex">
            {[
              { label: "About", href: "#about" },
              { label: "Products", href: "#products" },
              { label: "Contact", href: "#contact" },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-sm text-slate-300 transition-colors duration-200 hover:text-white"
                data-testid={`navbar-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <Button asChild className="rounded-full bg-cyan-300 px-5 text-sm font-semibold text-slate-950 hover:bg-cyan-200">
            <a href={portfolio.booking_url} target="_blank" rel="noreferrer" data-testid="navbar-booking-link">
              Work With Me
            </a>
          </Button>
        </div>
      </header>

      <main id="top" className="section-shell px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <section className="mx-auto grid max-w-7xl gap-10 pb-20 pt-6 lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:pb-28">
          <div className="reveal-up">
         
         

            <p className="section-kicker mt-8 text-xs font-semibold text-cyan-200/70">Dani Rosady •</p>
            <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-name-heading">
          
          
              <span className="block bg-gradient-to-r from-cyan-300 via-white to-amber-200 bg-clip-text text-transparent">
                {portfolio.profile.role}
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg" data-testid="hero-tagline-text">
              {portfolio.profile.tagline}
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base" data-testid="hero-intro-text">
              {portfolio.profile.intro}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-cyan-300 px-6 py-6 text-sm font-semibold text-slate-950 hover:bg-cyan-200">
                <a href={portfolio.booking_url} target="_blank" rel="noreferrer" data-testid="hero-work-with-me-button">
                  Work With Me
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent px-6 py-6 text-sm text-slate-100 hover:bg-white/5">
                <a href="#products" data-testid="hero-view-products-button">
                  View products
                  <ChevronRight className="h-4 w-4" />
                </a>
              </Button>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {portfolio.metrics.map((metric, index) => (
                <div
                  key={metric.value}
                  className="metric-pill rounded-[22px] p-4 reveal-up"
                  style={{ animationDelay: `${index * 120 + 120}ms` }}
                  data-testid={`hero-metric-${index + 1}`}
                >
                  <p className="text-xl font-semibold text-white">{metric.value}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-400">{metric.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {heroBadges.map((badge) => {
                const Icon = badge.icon;
                return (
                  <div key={badge.text} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200">
                    <Icon className="h-4 w-4 text-cyan-200" />
                    <span>{badge.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="hero-visual reveal-up" style={{ animationDelay: "180ms" }}>
            <div className="glass-panel overflow-hidden rounded-[32px] p-3">
              <div className="overflow-hidden rounded-[26px] border border-white/10 bg-slate-950/80">
                <img
                  src={portfolio.hero_image}
                  alt="Portfolio hero visual"
                  className="aspect-[4/3] w-full object-cover object-center"
                  data-testid="hero-visual-image"
                />
              </div>

              <div className="grid gap-3 p-4 md:grid-cols-3">
                {portfolio.profile.industries.map((industry) => (
                  <div
                    key={industry}
                    className="rounded-[22px] border border-white/10 bg-white/5 p-4"
                    data-testid={`industry-badge-${industry.toLowerCase()}`}
                  >
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Focus</p>
                    <p className="mt-3 text-lg font-semibold text-white">{industry}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="mx-auto max-w-7xl scroll-mt-28 pb-20">
          <div className="mb-10 max-w-3xl">
            <p className="section-kicker text-xs font-semibold text-cyan-200/70">About</p>
            <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl" data-testid="about-section-heading">
              A calm, modern delivery approach for software and IoT initiatives.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-400 sm:text-base">
              From discovery through delivery, the focus stays on operational clarity, responsive UX, and systems that make day-to-day decisions easier.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <Card className="glass-panel rounded-[28px] border-white/10">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 text-cyan-100">
                  <Workflow className="h-5 w-5" />
                  <p className="text-sm font-semibold uppercase tracking-[0.22em]">Core capabilities</p>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  {portfolio.profile.capabilities.map((item) => (
                    <div key={item} className="rounded-full bg-white/5 px-4 py-3 text-sm text-slate-100" data-testid={`capability-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-3">
              {portfolio.highlights.map((highlight, index) => (
                <Card
                  key={highlight.title}
                  className="tilt-card glass-panel rounded-[28px] border-white/10"
                  data-testid={`highlight-card-${index + 1}`}
                >
                  <CardContent className="p-8">
                    <div className="inline-flex rounded-full bg-cyan-300/10 p-3 text-cyan-200">
                      {index === 0 ? <CircuitBoard className="h-5 w-5" /> : index === 1 ? <Inbox className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-white" data-testid={`highlight-title-${index + 1}`}>
                      {highlight.title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-400">{highlight.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="products" className="mx-auto max-w-7xl scroll-mt-28 pb-20">
          <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <p className="section-kicker text-xs font-semibold text-cyan-200/70">Featured products</p>
             {/* <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl" data-testid="products-section-heading"> */}
                {/* Three highlighted projects with a stronger visual preview-first presentation. */}
              {/* </h2> */}
            </div>
            {/* <p className="max-w-xl text-sm leading-7 text-slate-400 sm:text-base"> */}
              {/* Teks dibuat lebih ringkas, blok yang tidak perlu dikurangi, dan fokus utama diarahkan ke visual preview project. */}
            {/* </p> */}
          </div>

          <div className="space-y-6">
            <Carousel
              setApi={setCarouselApi}
              opts={{ align: "start", loop: true }}
              className="overflow-hidden rounded-[32px]"
              data-testid="featured-products-carousel"
            >
              <CarouselContent>
                {featuredProducts.map((product) => (
                  <CarouselItem key={product.id}>
                    <ProductCard product={product} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                className="left-auto right-20 top-6 translate-y-0 border-white/10 bg-slate-950/70 text-white hover:bg-white/10"
                data-testid="featured-products-prev-button"
              />
              <CarouselNext
                className="right-6 top-6 translate-y-0 border-white/10 bg-slate-950/70 text-white hover:bg-white/10"
                data-testid="featured-products-next-button"
              />
            </Carousel>

            <div className="flex flex-wrap gap-3">
              {featuredProducts.map((product, index) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => carouselApi?.scrollTo(index)}
                  className={`rounded-full px-4 py-2 text-sm transition-colors duration-200 ${
                    activeSlide === index
                      ? "bg-cyan-300 text-slate-950"
                      : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                  }`}
                  data-testid={`featured-product-tab-${product.id}`}
                >
                  {product.title}
                </button>
              ))}
            </div>

            {moreProducts.length ? (
              <div className="glass-panel rounded-[28px] border border-white/10 p-6 sm:p-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200/70">More products</p>
                    {/* <h3 className="mt-3 text-2xl font-semibold text-white" data-testid="more-products-heading"> */}
                      {/* Project lain tetap tersedia, tapi disembunyikan dulu agar showcase utama tetap clean. */}
                    {/* </h3> */}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowMoreProducts((current) => !current)}
                    className="rounded-full border-white/15 bg-transparent px-5 py-6 text-sm text-slate-100 hover:bg-white/5"
                    data-testid="toggle-more-products-button"
                  >
                    {showMoreProducts ? "Hide More Products" : "More Products"}
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${showMoreProducts ? "rotate-90" : "rotate-0"}`} />
                  </Button>
                </div>

                <div
                  style={{
                    maxHeight: showMoreProducts ? "9999px" : "0px",
                    opacity: showMoreProducts ? 1 : 0,
                    transform: showMoreProducts ? "translateY(0)" : "translateY(-12px)",
                    overflow: "hidden",
                    transition: showMoreProducts
                      ? "max-height 0.65s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s"
                      : "max-height 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease, transform 0.25s ease",
                  }}
                >
                  <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {moreProducts.map((product) => (
                      <Card
                        key={product.id}
                        className="tilt-card glass-panel overflow-hidden rounded-[24px] border-white/10"
                        data-testid={`more-product-card-${product.id}`}
                      >
                        <div className="aspect-[16/10] overflow-hidden bg-slate-950/60">
                          <img
                            src={product.cover_image}
                            alt={`${product.title} preview`}
                            className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-[1.03]"
                            data-testid={`more-product-cover-${product.id}`}
                          />
                        </div>
                        <CardContent className="p-5">
                          <div className="flex flex-wrap gap-2">
                            <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300">{product.audience}</span>
                          </div>
                          <h4 className="mt-4 text-xl font-semibold text-white" data-testid={`more-product-title-${product.id}`}>
                            {product.title}
                          </h4>
                          <p className="mt-3 text-sm leading-7 text-slate-400">{product.summary}</p>
                          <Button asChild className="mt-5 rounded-full bg-cyan-300 px-5 py-5 text-sm font-semibold text-slate-950 hover:bg-cyan-200">
                            <a href={product.demo_url} target="_blank" rel="noreferrer" data-testid={`more-product-demo-link-${product.id}`}>
                              Book demo
                            </a>
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="mx-auto max-w-7xl pb-20">
          <div className="glass-panel rounded-[32px] border border-white/10 px-6 py-10 sm:px-10 lg:px-12 lg:py-12">
            <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="section-kicker text-xs font-semibold text-cyan-200/70">Work with Dani</p>
                <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl" data-testid="cta-heading">
                  Need a reliable technical partner for a software or IoT initiative?
                </h2>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
                  Book a focused conversation for product discovery, dashboard planning, field-operations tooling, or connected-device delivery.
                </p>
              </div>
              <Button asChild className="rounded-full bg-cyan-300 px-7 py-7 text-sm font-semibold text-slate-950 hover:bg-cyan-200">
                <a href={portfolio.booking_url} target="_blank" rel="noreferrer" data-testid="cta-booking-link">
                  <CalendarDays className="h-4 w-4" />
                  Book a project call
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section id="contact" className="mx-auto max-w-7xl scroll-mt-28 pb-12">
          <div className="mb-10 max-w-3xl">
            <p className="section-kicker text-xs font-semibold text-cyan-200/70">Contact</p>
            <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl" data-testid="contact-section-heading">
              Start the conversation.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-400 sm:text-base">
              Reach out directly or use the form to share your project scope, delivery timeline, and the outcome you want to achieve.
            </p>

          </div>

          <div className="grid gap-6 lg:grid-cols-[0.88fr_1.12fr]">
            <div className="space-y-4">
              {portfolio.contact_methods.map((method) => {
                const Icon = iconMap[method.kind] ?? Mail;
                return (
                  <a
                    key={method.kind}
                    href={method.href}
                    target={method.href.startsWith("http") ? "_blank" : undefined}
                    rel={method.href.startsWith("http") ? "noreferrer" : undefined}
                    className="block"
                    data-testid={`contact-method-${method.kind}`}
                  >
                    <Card className="tilt-card glass-panel rounded-[28px] border-white/10">
                      <CardContent className="flex items-center gap-4 p-6">
                        <div className="rounded-full bg-cyan-300/10 p-3 text-cyan-200">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.22em] text-slate-400">{method.label}</p>
                          <p className="mt-2 text-lg font-semibold text-white" data-testid={`contact-value-${method.kind}`}>
                            {method.value}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                );
              })}
            </div>

            <Card className="glass-panel rounded-[32px] border-white/10">
              <CardContent className="p-6 sm:p-8">
                <form className="grid gap-5" onSubmit={handleSubmit} data-netlify="true" name="contact" data-testid="contact-form">
                  <input type="hidden" name="form-name" value="contact" />
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-200" htmlFor="name">Name</label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="h-12 rounded-2xl border-white/10 bg-slate-950/60 text-white placeholder:text-slate-500"
                        data-testid="contact-form-name-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-200" htmlFor="email">Email</label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="h-12 rounded-2xl border-white/10 bg-slate-950/60 text-white placeholder:text-slate-500"
                        data-testid="contact-form-email-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-200" htmlFor="company">Company</label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Company or organization"
                        className="h-12 rounded-2xl border-white/10 bg-slate-950/60 text-white placeholder:text-slate-500"
                        data-testid="contact-form-company-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-200" htmlFor="project_type">Project type</label>
                      <Input
                        id="project_type"
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        placeholder="Portfolio site, dashboard, IoT platform..."
                        className="h-12 rounded-2xl border-white/10 bg-slate-950/60 text-white placeholder:text-slate-500"
                        data-testid="contact-form-project-type-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-200" htmlFor="message">Project details</label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell Dani about the scope, audience, and what success looks like."
                      className="min-h-[160px] rounded-[24px] border-white/10 bg-slate-950/60 text-white placeholder:text-slate-500"
                      data-testid="contact-form-message-textarea"
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-4 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm leading-6 text-slate-400" data-testid="contact-form-helper-text">
                      Best for enterprise sites, IoT systems, monitoring dashboards, and stakeholder-facing delivery tools.
                    </p>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="rounded-full bg-cyan-300 px-6 py-6 text-sm font-semibold text-slate-950 hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-70"
                      data-testid="contact-form-submit-button"
                    >
                      <Send className="h-4 w-4" />
                      {isSubmitting ? "Sending..." : "Send inquiry"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        <footer className="mx-auto max-w-7xl border-t border-white/10 pt-8 text-sm text-slate-500">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p data-testid="footer-brand-text">Dani Rosady — software developer and IoT engineer.</p>
            <a href={portfolio.booking_url} target="_blank" rel="noreferrer" className="text-slate-300 transition-colors duration-200 hover:text-white" data-testid="footer-booking-link">
              Book a project call
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
};